<template>
    <div class="module">
        <section class="module__body">
            <div class="title-block">
                <div class="title-block__icon" />
                <div class="title-block__body">
                    <h1>Users</h1>
                    <span class="user-count">
                        <span data-test-id="title-user-count">{{ userCount }}</span>
                        users
                    </span>
                </div>
                <div class="title-block__button">
                    <router-link
                        v-if="can('create privileged users')"
                        :to="{ name: 'super-create-privileged-user' }"
                        class="btn btn-primary"
                        data-test-id="create-privileged-user-button"
                        tag="button"
                    >
                        Create Privileged User
                    </router-link>
                </div>
            </div>

            <SelectButton
                v-model="currentViewId"
                :allowEmpty="false"
                :options="options"
                optionValue="id"
                optionLabel="label"
                fluid
            >
                <template #option="slotProps">
                    <span :data-test-id="`search-view-buton-${slotProps.option.id}`">{{ slotProps.option.label }}</span>
                </template>
            </SelectButton>
            <Search
                :columns="currentView.columns"
                :view="currentViewId"
                @page-loaded="searchPageLoaded"
            />
        </section>
    </div>
</template>

<script>
import moment from 'moment-timezone'
import SelectButton from 'primevue/selectbutton'
import Search from '@/components/Search/index.vue'
import Permissions from '@/mixins/permissions'
import { userRoleOptions } from '@/constants/user-roles'
import { yesNoOptions } from '@/constants/yes-no'

const timezoneString = moment().tz(moment.tz.guess()).format('z')

const columnDefinitions = {
    name: {
        dataType: 'string',
        field: 'name',
        filterable: true,
        label: 'Name',
        sortable: true,
    },
    username: {
        dataType: 'string',
        field: 'username',
        filterable: true,
        label: 'Username',
        sortable: true,
    },
    email: {
        dataType: 'string',
        field: 'email',
        filterable: true,
        label: 'Email',
        sortable: true,
    },
    title: {
        dataType: 'string',
        field: 'title',
        filterable: true,
        label: 'Title',
        sortable: true,
    },
    organization: {
        dataType: 'string',
        field: 'organization',
        filterable: true,
        label: 'Organization',
        sortable: true,
    },
    roles: {
        dataType: 'enum',
        field: 'roles',
        filterable: true,
        label: 'Roles',
        options: userRoleOptions,
        sortable: true,
    },
    confirmed: {
        dataType: 'enum',
        field: 'confirmed',
        filterable: true,
        label: 'Confirmed',
        options: yesNoOptions,
        sortable: true,
    },
    lastLoginAt: {
        dataType: 'datetime',
        field: 'last_login_at',
        label: `Last Login (${timezoneString})`,
        sortable: true,
        filterable: true,
    },
    createdAt: {
        dataType: 'datetime',
        field: 'created_at',
        label: `Account Created (${timezoneString})`,
        sortable: true,
        filterable: true,
    },
    actions: {
        dataType: 'actions',
        field: 'actions',
        sortable: false,
    },
}

const views = [
    {
        id: 'all_users',
        label: 'All Users',
        columns: [
            columnDefinitions.name,
            columnDefinitions.email,
            columnDefinitions.organization,
            columnDefinitions.roles,
            columnDefinitions.lastLoginAt,
            columnDefinitions.createdAt,
            columnDefinitions.actions,
        ],
    },
    {
        id: 'privileged_users',
        label: 'Privileged Users',
        columns: [
            columnDefinitions.name,
            columnDefinitions.username,
            columnDefinitions.email,
            columnDefinitions.title,
            columnDefinitions.roles,
            columnDefinitions.confirmed,
            columnDefinitions.lastLoginAt,
            columnDefinitions.actions,
        ],
    },
    {
        id: 'organization_users',
        label: 'Organization Users',
        columns: [
            columnDefinitions.name,
            columnDefinitions.email,
            columnDefinitions.organization,
            columnDefinitions.roles,
            columnDefinitions.lastLoginAt,
            columnDefinitions.createdAt,
            columnDefinitions.actions,
        ],
    },
]

export default {
    components: {
        Search,
        SelectButton,
    },

    mixins: [Permissions],

    data() {
        const firstView = views[0]
        return {
            currentViewId: firstView.id,
            options: views,
            userCount: 0,
        }
    },

    computed: {
        currentView() {
            return views.find(({ id }) => id === this.currentViewId)
        },
    },

    methods: {
        searchPageLoaded(count, totalCount) {
            this.userCount = totalCount
        },
    },
}
</script>

<style lang="scss" scoped>
.module__body {
    .title-block {
        display: flex;
        width: 100%;
        margin-bottom: 20px;
        &__icon {
            width: 52px;
            height: 52px;
            background-color: #212628;
            background-image: url(@images/group-icon.svg);
            background-repeat: no-repeat;
            background-position: center center;
            background-size: 30px 30px;
            border-radius: 5px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 36px;
            margin-right: 10px;
        }
        &__body {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-self: stretch;
            margin: 2px 0;
            h1 {
                font-size: 24px;
                display: flex;
                align-items: center;
            }
        }
        &__button {
            margin-left: auto;
        }
    }
}
</style>
